/**
 * "Join with a code", which reveals the box you type it into.
 *
 * Closed, it is a button across the width of the action. Pressed, the button
 * slides to the right and the code input is uncovered in the space it leaves —
 * the input was always there, underneath.
 *
 * ## Why this is allowed to replace a permanent form
 *
 * D2 and D3 originally kept a code form on the page at all times, because
 * `Landing.tsx` had this constraint and it is still true:
 *
 * > Most people arriving here have been handed a four-character code by
 * > somebody in the room, and they should not have to look past a sign-up form
 * > to use it.
 *
 * What that was protecting against was the code path being **buried** — put
 * behind a page change, under marketing, or below the fold. This buries
 * nothing: the control is one of the two primary actions, above the fold, and
 * it costs one tap to reach an input that is already focused when it arrives.
 *
 * ## The parts that are not decoration
 *
 * Every behavior on the input is carried over unchanged from the form this
 * replaces. Each one exists because somebody hit something:
 *
 * - `onFocus` selects, so a returning player's old code is replaced by their
 *   first keystroke rather than backspaced away four times;
 * - `autoCapitalize`/`autoCorrect`/`spellCheck` off, because a code is read off
 *   a television and typed on a phone;
 * - `maxLength` and the disabled rule, so a half-typed code cannot be sent.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../render/Icon.js';

const CODE_KEY = 'cards.lastRoom';

export function JoinWithCode() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [code, setCode] = useState(() => {
    try {
      return window.localStorage.getItem(CODE_KEY) ?? '';
    } catch {
      return '';
    }
  });

  const ready = code.trim().length >= 4;

  /*
   * Focus follows the reveal.
   *
   * Somebody who presses "join with a code" is holding a code and wants to type
   * it. Making them tap the box that just appeared is asking for a second tap
   * to do the thing the first one already said.
   */
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /*
   * Escape puts it away.
   *
   * Opened by accident, this otherwise covers the other action with a box the
   * only way out of which is to find something else to press.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const join = () => {
    const tidy = code.trim().toUpperCase();
    if (tidy.length < 4) return;
    navigate(`/join/${tidy}`);
  };

  /* Enter in the input. The form has exactly one field, so the browser submits
     it implicitly even though no button is `type="submit"` any more — see the
     note on the button for why none of them can be. */
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    join();
  };

  return (
    <form className={open ? 'join-code open' : 'join-code'} onSubmit={submit}>
      <input
        ref={inputRef}
        className="join-code-input"
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        onFocus={(event) => {
          const input = event.currentTarget;
          // A tick later than the browser's own handling: Safari and Chrome
          // both place the caret AFTER focus, so selecting immediately loses.
          window.setTimeout(() => input.select(), 0);
        }}
        maxLength={8}
        placeholder="ABCD"
        aria-label="Room code"
        /*
         * Hidden from assistive tech while it is covered, rather than merely
         * invisible. A box a screen reader can reach but a sighted user cannot
         * see is worse than either.
         */
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
      />

      {/*
        One button, which moves.

        Not two that swap: the point of the interaction is that the button
        *travels* to the right and uncovers the box it was sitting on, and a
        component that unmounts one element and mounts another has nothing to
        animate - it would cut, not slide. So the same element stays mounted,
        changes what it says and what it does, and its `left` is transitioned.

        ## It is a `type="button"` in both states, and that is load-bearing

        It used to be `type={open ? 'submit' : 'button'}`, which navigated
        straight to /join/<whatever was in localStorage> the moment you pressed
        it - the panel opened and was gone again inside the same tick.

        React flushes a discrete event's state update synchronously, during the
        dispatch. The browser runs a button's activation behavior AFTER the
        dispatch finishes. So `open` had already become true, the attribute had
        already become `submit`, and the browser submitted the form on the
        strength of a click that had been aimed at a plain button.

        A control whose type changes under its own click cannot be reasoned
        about, so this one never changes: it stays a plain button and calls
        `join()` itself. Enter in the input still submits the form - one field
        and no submit button is exactly the case the implicit submission rules
        cover - so `onSubmit` is still real, and still the only submit path.
      */}
      <button
        type="button"
        className="join-code-button"
        onClick={open ? join : () => setOpen(true)}
        // Disabled only once it can join; disabling the closed state would
        // make the control unopenable before anything had been typed.
        disabled={open && !ready}
        aria-expanded={open}
      >
        {open ? (
          'Join'
        ) : (
          <>
            <Icon name="join-group" className="cta-mark" />
            <span className="cta-label">Join with a Code</span>
          </>
        )}
      </button>
    </form>
  );
}
